import * as Sentry from '@sentry/react';
import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { API_ENDPOINTS } from '../utils/constants';
import { parseApiResponse } from '../utils/api-errors';
import { getIdentityGeneration, registerIdentityRequest } from '../utils/identity-scope';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_ENDPOINTS.KARLA_FLASK_API,
  credentials: 'include',
  responseHandler: parseApiResponse,
});

export const API_TAG_TYPES = ['Notification', 'AdminConversation', 'Integrations', 'Conversation', 'ConversationList', 'ConversationLogs', 'Model', 'Models', 'ModelVersion', 'Datastream', 'Datastreams', 'StreamFiles', 'StoredText', 'ApiKey', 'Organization', 'ApiUser', 'OperationsRateLimits'] as const;
export type ApiTagType = (typeof API_TAG_TYPES)[number];

const publicRoutes = ['/login', '/oauth/authorize', '/reset-password', '/sign-up', '/accept-invite', '/verify-email', '/share', '/admin-consent/success', '/admin-consent/error'];

let redirectingToLogin = false;

const isPublicRoute = (pathname: string) => publicRoutes.some((route) => pathname === route || pathname.startsWith(`${route}/`));

const baseQueryWithSentry: typeof rawBaseQuery = async (args, api, extraOptions) => {
  const generation = getIdentityGeneration();
  const controller = new AbortController();
  const unregister = registerIdentityRequest(controller);
  let result;
  try {
    result = await rawBaseQuery(args, { ...api, signal: AbortSignal.any([api.signal, controller.signal]) }, extraOptions);
  } finally {
    unregister();
  }
  if (generation !== getIdentityGeneration()) return { error: { status: 'CUSTOM_ERROR', error: 'Identity changed; response ignored.' } };
  if (result.error?.status === 'PARSING_ERROR') {
    result = { ...result, error: { status: result.error.originalStatus, data: { type: 'error', reason: `Request failed (HTTP ${result.error.originalStatus}).` } } };
  }
  if (result.error) {
    const err = result.error as FetchBaseQueryError;
    if (err.status === 401 && typeof window !== 'undefined' && !isPublicRoute(window.location.pathname) && !redirectingToLogin) {
      redirectingToLogin = true;
      Sentry.setUser(null);
      window.location.assign(`/login?returnUrl=${encodeURIComponent(window.location.pathname)}`);
    } else if (typeof err.status === 'number' && err.status !== 401) {
      const url = typeof args === 'string' ? args : (args as { url: string }).url;
      Sentry.captureException(new Error(`API ${err.status}: ${url}`), {
        extra: { status: err.status, data: err.data },
      });
    }
  }
  return result;
};

export const baseApi = createApi({
  baseQuery: baseQueryWithSentry,
  endpoints: () => ({}),
  tagTypes: API_TAG_TYPES,
});
